<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { Bell } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { 
    Popover, 
    PopoverContent, 
    PopoverTrigger 
} from '@/components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { useNotifications } from '@/hooks/useNotifications';
import NotificationList from './NotificationList.vue';
import api from '@/lib/axios';
import { useToast } from '@/hooks/use-toast';

const { t } = useI18n();
const { toast } = useToast();
const router = useRouter();

const {
    notifications,
    unreadNotifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead
} = useNotifications();

const isOpen = ref(false);
const activeTab = ref('all');

const displayedNotifications = computed(() => {
    return activeTab.value === 'unread' 
        ? unreadNotifications.value 
        : notifications.value;
});

const handleMarkAsRead = async (notificationId) => {
    try {
        await markAsRead(notificationId);
    } catch (error) {
        console.error('Failed to mark as read:', error);
    }
};

const handleMarkAllAsRead = async () => {
    try {
        await markAllAsRead();
    } catch (error) {
        console.error('Failed to mark all as read:', error);
    }
};

const handleNotificationClicked = async (notification) => {
    const data = notification?.data || {};
    const type = data.type || notification.type;

    // Alert triggered — navigate to alerts page with history tab
    if (type === 'alert_triggered' || type === 'AlertTriggeredNotification') {
        isOpen.value = false;
        router.push({
            path: '/alerts',
            query: { tab: 'history', alert_id: data.alert_id || '' }
        });
        return;
    }

    if (type !== 'activity_logs_export' && type !== 'ActivityLogsExportNotification') return;
    if (!data.download_url) return;

    try {
        const response = await api.get(data.download_url, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        const disposition = response.headers?.['content-disposition'];
        const filenameMatch = disposition?.match(/filename="?([^";]+)"?/);
        const filename = filenameMatch?.[1] || data.filename || 'activity_logs.csv';

        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Failed to download export:', error);
        toast({
            title: t('activities.common.error'),
            description: t('notifications.download_error')
        });
    }
};
</script>

<template>
    <Popover v-model:open="isOpen">
        <PopoverTrigger asChild>
            <Button 
                variant="ghost" 
                size="icon" 
                class="relative w-9 h-9 rounded-full bg-background/50 backdrop-blur-sm border border-border/50 hover:bg-accent"
                :aria-label="t('notifications.title')"
            >
                <Bell class="h-4 w-4 text-foreground" />
                
                <!-- Unread count badge -->
                <span 
                    v-if="unreadCount > 0"
                    class="absolute -top-1.5 -right-1.5 flex items-center justify-center min-w-[16px] h-[16px] px-1 text-[9px] font-bold text-white bg-red-500 rounded-full ring-2 ring-background shadow-sm"
                >
                    {{ unreadCount > 99 ? '99+' : unreadCount }}
                </span>
            </Button>
        </PopoverTrigger>

        <PopoverContent 
            align="end" 
            :side-offset="8"
            class="w-[380px] p-0 overflow-hidden"
        >
            <!-- Header -->
            <div class="px-4 py-3 border-b border-border/50 bg-muted/30">
                <h3 class="font-semibold text-foreground">
                    {{ t('notifications.title') }}
                </h3>
            </div>

            <!-- Tabs -->
            <Tabs v-model="activeTab" class="w-full">
                <div class="px-2 pt-2 bg-muted/20">
                    <TabsList class="w-full grid grid-cols-2 h-9">
                        <TabsTrigger value="all" class="text-xs">
                            {{ t('notifications.all') }}
                        </TabsTrigger>
                        <TabsTrigger value="unread" class="text-xs gap-1.5">
                            {{ t('notifications.unread') }}
                            <Badge 
                                v-if="unreadCount > 0" 
                                variant="secondary" 
                                class="h-5 min-w-[20px] px-1.5 text-[10px]"
                            >
                                {{ unreadCount }}
                            </Badge>
                        </TabsTrigger>
                    </TabsList>
                </div>

                <TabsContent value="all" class="m-0">
                    <NotificationList 
                        :notifications="notifications" 
                        :is-loading="isLoading"
                        @mark-as-read="handleMarkAsRead"
                        @mark-all-as-read="handleMarkAllAsRead"
                        @notification-clicked="handleNotificationClicked"
                    />
                </TabsContent>

                <TabsContent value="unread" class="m-0">
                    <NotificationList 
                        :notifications="unreadNotifications" 
                        :is-loading="isLoading"
                        @mark-as-read="handleMarkAsRead"
                        @mark-all-as-read="handleMarkAllAsRead"
                        @notification-clicked="handleNotificationClicked"
                    />
                </TabsContent>
            </Tabs>
        </PopoverContent>
    </Popover>
</template>
